/**
 * ReaderJourney.jsx — Feb 9 2026, user spec: "Wire journey to top reader
 * and mirror journey to top scribe."
 *
 * Mirror of WriterJourney.jsx — same hero / share-code / next-milestone
 * / total-coins / achievements / roadmap structure, but driven by the
 * reader-side data (listening minutes instead of words, listening
 * milestones instead of word milestones, summit "The Voracious" instead
 * of "Legendary Archon").
 *
 * Sections:
 *   1. Hero — lifetime listening minutes + progress to next tier
 *   2. Share-my-code card (referrals — same payload as writer page)
 *   3. Next milestone callout (coin reward + minutes remaining)
 *   4. Total Coins Earned from Journey card
 *   5. Achievements grid — every listening milestone awarded
 *   6. Quick-stat row (Streak · Level · Quizzes — link to breakdown tabs)
 *   7. Roadmap to The Voracious (Lv 91-100)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Crown, Trophy, Coins, ArrowLeft, Headphones, Sparkles, Share2, Copy,
  Check, Users, BarChart3, Flame, BookOpen, Brain, Zap, ChevronRight,
} from 'lucide-react';
import { authFetch, checkAuth } from '../utils/authUtils';
import JourneyRoadmap from '../components/JourneyRoadmap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COIN_TO_GBP = 0.01;

const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};
const fmtMinutes = (mins) => {
  const m = Number(mins || 0);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

// ── Reader counterpart of the Writer ShareCodeCard. Same data source
//    (`/api/codes/mine`), same UTM tagging logic; only the copy differs
//    so the framing reads as a reader-to-reader invite.
const ShareCodeCard = ({ codeMeta }) => {
  const [copied, setCopied] = useState(null);
  if (!codeMeta || !codeMeta.unique_code) return null;
  const code = codeMeta.unique_code;
  const utmTag = code.startsWith('SY-W-') ? 'author'
    : code.startsWith('SY-M-') ? 'ambassador'
    : 'reader';
  const qs = `ref=${encodeURIComponent(code)}&utm=${utmTag}`;
  const inviteUrl = `${window.location.origin}/?${qs}`;
  const sharePayload = {
    title: 'Join me on Smarter You',
    text: `Use my code ${code} to sign up — we both earn coins.`,
    url: inviteUrl,
  };
  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1800);
    } catch { /* ignore */ }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share(sharePayload); return; }
      catch { /* user dismissed */ }
    }
    copy(inviteUrl, 'url');
  };
  const linkedSubs = codeMeta.linked_subscribers || 0;
  const earned = codeMeta.recent_events_coin_total || 0;
  return (
    <section
      className="rounded-2xl bg-gradient-to-br from-emerald-900/40 via-slate-800/60 to-amber-900/30 border border-emerald-500/30 p-5 sm:p-6 mb-5"
      data-testid="reader-journey-share-code-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Share2 className="w-5 h-5 text-emerald-300" />
        <h3 className="text-white font-bold text-base sm:text-lg">Share your reader code</h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-emerald-300 inline-flex items-center gap-1">
          <Users className="w-3 h-3" />
          {linkedSubs} signup{linkedSubs === 1 ? '' : 's'}
        </span>
      </div>
      <p className="text-[12px] text-slate-300/90 mb-3">
        Every friend who signs up with your code earns you 50 coins · 25 more when they hit 30 min of listening · 25 on first finished book.
        {earned > 0 && (
          <>
            {' '}You&apos;ve earned{' '}
            <strong className="text-amber-200">{earned.toLocaleString()} coins</strong>
            {' '}from referrals so far.
          </>
        )}
      </p>
      <div className="flex flex-wrap items-stretch gap-2">
        <button
          type="button"
          onClick={() => copy(code, 'code')}
          className="flex-1 min-w-[150px] inline-flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-slate-950/60 border border-emerald-400/40 text-emerald-100 font-mono font-bold tracking-wider hover:bg-slate-900"
          data-testid="reader-journey-copy-code"
        >
          <span className="truncate">{code}</span>
          {copied === 'code' ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4 text-slate-300" />}
        </button>
        <button
          type="button"
          onClick={() => copy(inviteUrl, 'url')}
          className="flex-1 min-w-[150px] inline-flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-slate-900/50 border border-slate-600 text-slate-200 text-xs hover:bg-slate-800"
          data-testid="reader-journey-copy-invite-url"
        >
          <span className="truncate">{inviteUrl}</span>
          {copied === 'url' ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4 text-slate-300" />}
        </button>
        <button
          type="button"
          onClick={share}
          className="px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-sm inline-flex items-center gap-1.5"
          data-testid="reader-journey-share-code"
        >
          <Share2 className="w-4 h-4" /> Share
        </button>
      </div>
    </section>
  );
};

const ReaderJourney = () => {
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [readerStats, setReaderStats] = useState(null);
  const [codeMeta, setCodeMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    const u = await checkAuth();
    if (!u) { navigate('/login'); return; }
    const [j, s, cm] = await Promise.allSettled([
      authFetch(`${BACKEND_URL}/api/reader/journey`).then((r) => r.json()),
      authFetch(`${BACKEND_URL}/api/reader/breakdown`).then((r) => r.json()),
      authFetch(`${BACKEND_URL}/api/codes/mine`).then((r) => r.json()),
    ]);
    if (j.status === 'fulfilled') setJourney(j.value);
    if (s.status === 'fulfilled') setReaderStats(s.value);
    if (cm.status === 'fulfilled') setCodeMeta(cm.value || null);
    setLoading(false);
  }, [navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-gray-400">Loading your journey…</p>
      </div>
    );
  }

  const lifetimeMinutes = journey?.lifetime_minutes || 0;
  const next = journey?.next_milestone;
  const trigger = next?.threshold_minutes || 60;
  // Same visual logic as writer page — fill % toward the next milestone.
  const pct = next
    ? Math.min(100, ((trigger - next.remaining) / trigger) * 100)
    : 100;
  const achievements = journey?.achievements || [];
  const totalCoinsFromJourney = journey?.total_coins_from_listening || 0;
  const streak = readerStats?.streak?.current || 0;
  const level = readerStats?.overall_level?.level || readerStats?.level?.value || 1;
  const tierTitle = readerStats?.overall_level?.tier || readerStats?.level?.tier || 'Novice Explorer';
  const quizBest = readerStats?.quiz_demo_best_correct || readerStats?.reading_stats?.quiz_demo_best_correct || 0;

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="reader-journey-page">
      <div className="flex items-center gap-2 mb-5">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-white/5" data-testid="reader-journey-back">
          <ArrowLeft className="w-4 h-4 text-white/60" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-emerald-300">Reader path</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight leading-none mt-1">
            Journey to Top Reader<span className="text-emerald-300">.</span>
          </h1>
        </div>
        <button
          type="button"
          onClick={() => navigate('/reader-journey/breakdown')}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-emerald-500/15 border border-emerald-400/30 hover:border-emerald-400/60 text-emerald-300 text-[10px] font-black uppercase tracking-[0.2em]"
          data-testid="reader-journey-open-breakdown"
          title="Streak / XP / Level breakdown"
        >
          <BarChart3 className="w-3.5 h-3.5" />
          Breakdown
        </button>
      </div>

      {/* Hero — lifetime listening minutes */}
      <section
        className="rounded-2xl bg-gradient-to-br from-emerald-900/40 via-slate-800/60 to-sky-900/30 border border-emerald-500/30 p-5 sm:p-6 mb-5"
        data-testid="reader-journey-hero"
      >
        <p className="text-[11px] uppercase tracking-widest font-bold text-emerald-300">Lifetime Listening</p>
        <h2
          className="text-4xl sm:text-5xl font-black text-white mt-1 tabular-nums"
          data-testid="reader-journey-lifetime-minutes"
        >
          {fmtNum(lifetimeMinutes)}
          <span className="text-xl sm:text-2xl text-emerald-200/70 font-bold ml-2">min</span>
        </h2>
        <p className="text-[11px] text-slate-400 mt-1">
          {fmtMinutes(lifetimeMinutes)} across audiobooks + cinematic series
        </p>

        {next && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-slate-300 mb-1.5">
              <span>Next milestone — {fmtMinutes(trigger)}</span>
              <span className="tabular-nums font-bold text-amber-200">{Math.floor(pct)}%</span>
            </div>
            <div className="h-2 rounded-full bg-slate-900/60 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 via-sky-500 to-amber-400 transition-all"
                style={{ width: `${pct}%` }}
                data-testid="reader-journey-progress-bar"
              />
            </div>
          </div>
        )}

        {/* Quick action chips */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => navigate('/reader-journey/breakdown?focus=streak')}
            className="rounded-xl bg-slate-900/50 hover:bg-slate-800 px-2 py-2 transition-colors text-center"
            data-testid="reader-journey-quickstat-streak"
          >
            <Flame className="w-4 h-4 text-orange-300 mx-auto" />
            <p className="text-[9px] uppercase tracking-widest text-orange-300 font-bold mt-1">Streak</p>
            <p className="text-sm font-black text-white tabular-nums">{fmtNum(streak)}<span className="text-[9px] text-slate-400 font-medium ml-1">d</span></p>
          </button>
          <button
            type="button"
            onClick={() => navigate('/reader-journey/breakdown?focus=level')}
            className="rounded-xl bg-slate-900/50 hover:bg-slate-800 px-2 py-2 transition-colors text-center"
            data-testid="reader-journey-quickstat-level"
          >
            <Trophy className="w-4 h-4 text-amber-300 mx-auto" />
            <p className="text-[9px] uppercase tracking-widest text-amber-300 font-bold mt-1">Level</p>
            <p className="text-sm font-black text-white tabular-nums">{level}</p>
          </button>
          <button
            type="button"
            onClick={() => navigate('/quiz-demo')}
            className="rounded-xl bg-slate-900/50 hover:bg-slate-800 px-2 py-2 transition-colors text-center"
            data-testid="reader-journey-quickstat-quiz"
          >
            <Brain className="w-4 h-4 text-fuchsia-300 mx-auto" />
            <p className="text-[9px] uppercase tracking-widest text-fuchsia-300 font-bold mt-1">Quiz best</p>
            <p className="text-sm font-black text-white tabular-nums">{fmtNum(quizBest)}<span className="text-[9px] text-slate-400 font-medium ml-1">/5</span></p>
          </button>
        </div>

        <p className="text-[11px] text-slate-300 mt-3 italic">
          You are <strong className="text-emerald-200">{tierTitle}</strong>. Keep climbing.
        </p>
      </section>

      {/* Share-my-code card */}
      <ShareCodeCard codeMeta={codeMeta} />

      {/* Reading Training launcher — Feb 9 2026 user spec.
          Primary CTA: enter the comprehensive training program (flash +
          chunk reading + real XP). This is the headline action a reader
          should be taking from the Journey page. */}
      <section
        className="rounded-2xl border-2 border-emerald-400/60 bg-gradient-to-br from-emerald-900/40 via-slate-900/40 to-amber-900/30 p-5 mb-5 shadow-[0_0_40px_rgba(16,185,129,0.15)]"
        data-testid="reader-journey-training-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-full bg-emerald-400/20 grid place-items-center flex-shrink-0">
            <Brain className="w-6 h-6 text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-black">Reading Training Program</p>
            <h3 className="text-white font-black text-lg mt-0.5">Train flash + chunk reading</h3>
            <p className="text-[12px] text-slate-300/90 mt-1">
              5-passage sessions. <strong className="text-emerald-200">+10 XP</strong> per passage,{' '}
              <strong className="text-cyan-200">+10 XP</strong> per correct answer,{' '}
              <strong className="text-rose-300">−5 XP</strong> for missed retention. Speed + comprehension tracked over time.
            </p>
            <button
              type="button"
              onClick={() => navigate('/reader-training')}
              className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-black text-xs uppercase tracking-widest"
              data-testid="reader-journey-start-training-btn"
            >
              Start training <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Next milestone callout */}
      {next && (
        <section
          className="rounded-2xl bg-amber-900/20 border border-amber-500/30 p-4 mb-5 flex items-center gap-3"
          data-testid="reader-journey-next-milestone"
        >
          <Sparkles className="w-5 h-5 text-amber-300 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-white text-sm font-bold">
              Next: <span className="text-amber-200">{fmtMinutes(next.threshold_minutes)}</span>
              {' '}<span className="text-amber-200">· +{next.coins_reward} coins</span>
            </p>
            <p className="text-xs text-amber-200/80">
              {fmtMinutes(next.remaining)} of listening left to unlock this milestone.
            </p>
          </div>
        </section>
      )}

      {/* Total coins earned card — surfaced above achievements */}
      <section
        className="rounded-2xl bg-gradient-to-r from-amber-900/40 via-yellow-900/30 to-amber-900/40 border border-amber-500/40 p-4 mb-4 flex items-center justify-between gap-3"
        data-testid="reader-journey-coins-earned-card"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-amber-500/20 border border-amber-400/50 flex items-center justify-center flex-shrink-0">
            <Coins className="w-5 h-5 text-amber-200" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-amber-200/80 font-bold">
              Total Coins Earned from Journey
            </p>
            <p
              className="text-2xl sm:text-3xl font-black text-white tabular-nums leading-tight"
              data-testid="reader-journey-coins-earned-value"
            >
              {fmtNum(totalCoinsFromJourney)}
              <span className="text-xs font-medium text-amber-200/80 ml-2">
                ≈ £{(totalCoinsFromJourney * COIN_TO_GBP).toFixed(2)}
              </span>
            </p>
          </div>
        </div>
        <div className="text-right hidden sm:block">
          <p className="text-[10px] uppercase tracking-widest text-amber-200/60 font-bold">
            Milestones earned
          </p>
          <p className="text-xl font-black text-amber-200 tabular-nums">
            {achievements.length}
          </p>
        </div>
      </section>

      {/* Achievements grid */}
      <section className="mt-6">
        <h3 className="text-sm uppercase tracking-widest font-bold text-slate-400 mb-3 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-300" /> Listening Milestones
          <span className="ml-2 text-[10px] font-bold bg-slate-800 text-slate-300 px-2 py-0.5 rounded-full tabular-nums">
            {achievements.length}
          </span>
        </h3>
        {achievements.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="reader-journey-achievements-empty">
            No milestones yet — listen for 60 minutes to unlock your first (+100 coins).
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="reader-journey-achievements-grid">
            {achievements.map((a) => (
              <div
                key={a.milestone_key}
                className="rounded-xl bg-slate-800/60 border border-emerald-400/30 p-3"
                data-testid={`reader-journey-achievement-${a.milestone_key}`}
              >
                <div className="flex items-center justify-between">
                  <Headphones className="w-5 h-5 text-emerald-300" />
                  <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-300">
                    {a.milestone_key}
                  </span>
                </div>
                <p className="text-white font-bold text-sm mt-2 tabular-nums">+{a.coins_awarded} coins</p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {fmtMinutes(a.threshold_minutes)} · {fmtDate(a.awarded_at)}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Reader actions row — bookshelf + speed-test + quiz */}
      <section className="mt-6 grid grid-cols-3 gap-2" data-testid="reader-journey-actions">
        <button
          type="button"
          onClick={() => navigate('/library')}
          className="rounded-xl bg-gradient-to-br from-amber-900/40 to-slate-900/60 border border-amber-400/30 p-3 text-center hover:border-amber-300 transition"
          data-testid="reader-journey-action-library"
        >
          <BookOpen className="w-5 h-5 text-amber-300 mx-auto" />
          <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mt-1">Library</p>
          <p className="text-[11px] text-slate-400">Pick your next read</p>
        </button>
        <button
          type="button"
          onClick={() => navigate('/speed-test')}
          className="rounded-xl bg-gradient-to-br from-rose-900/40 to-slate-900/60 border border-rose-400/30 p-3 text-center hover:border-rose-300 transition"
          data-testid="reader-journey-action-speed"
        >
          <Zap className="w-5 h-5 text-rose-300 mx-auto" />
          <p className="text-[10px] uppercase tracking-widest text-rose-300 font-bold mt-1">Speed</p>
          <p className="text-[11px] text-slate-400">Test your wpm</p>
        </button>
        <button
          type="button"
          onClick={() => navigate('/quiz-demo')}
          className="rounded-xl bg-gradient-to-br from-fuchsia-900/40 to-slate-900/60 border border-fuchsia-400/30 p-3 text-center hover:border-fuchsia-300 transition"
          data-testid="reader-journey-action-quiz"
        >
          <Brain className="w-5 h-5 text-fuchsia-300 mx-auto" />
          <p className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold mt-1">Retention</p>
          <p className="text-[11px] text-slate-400">Flash + quiz</p>
        </button>
      </section>

      {/* Footer hint — milestone economy */}
      <div className="mt-6 flex items-center gap-2 text-[11px] text-slate-500">
        <Headphones className="w-3.5 h-3.5" />
        <span>Coin rewards: 60m = +100 · 5h = +300 · Every +10h thereafter = +100</span>
      </div>

      {/* Roadmap to The Voracious — tier-by-tier climb. Lives at the
          bottom mirroring the WriterJourney layout. */}
      <section className="mt-10">
        <JourneyRoadmap kind="reader" />
      </section>
    </main>
  );
};

export default ReaderJourney;
